import { useEffect } from 'react'

import { useBlocker, useRouter } from '@tanstack/react-router'
import { handleNotAwaitedPromise } from '@valian/react-kitchen-sink'

import { SESSION_EXPIRED_DIALOG_ATTRIBUTE } from '@/components/organisms/SessionExpiredDialog'

import {
  canProceed,
  confirmSessionThenOpenDialog,
  isPinned,
  openSessionDialog,
  recordActivity,
  sessionPhase,
} from './sessionStore.ts'

const GUARDED_EVENTS = [
  'pointerdown',
  'mousedown',
  'touchstart',
  'pointerup',
  'mouseup',
  'touchend',
  'click',
  'dblclick',
  'auxclick',
  'contextmenu',
  'keydown',
  'keyup',
  'submit',
] as const

const ACTIVITY_EVENTS = new Set<string>(['pointerdown', 'click', 'keydown', 'submit'])

const GESTURE_TAIL_MS = 1000

const ACTION_SELECTOR = [
  'a[href]',
  'button',
  'select',
  'summary',
  'label',
  'input[type=checkbox]',
  'input[type=radio]',
  'input[type=submit]',
  'input[type=button]',
  'input[type=reset]',
  '[role=button]',
  '[role=link]',
  '[role=menuitem]',
  '[role=menuitemcheckbox]',
  '[role=menuitemradio]',
  '[role=tab]',
  '[role=option]',
  '[role=checkbox]',
  '[role=radio]',
  '[role=switch]',
  '[role=combobox]',
].join(',')

const FORM_FIELD_SELECTOR = 'input, select'

const elementOf = (target: EventTarget | null) => (target instanceof Element ? target : null)

const isInsideSessionDialog = (target: EventTarget | null) =>
  elementOf(target)?.closest(`[${SESSION_EXPIRED_DIALOG_ATTRIBUTE}]`) != null

const looksClickable = (element: Element) => {
  for (let current: Element | null = element; current && current !== document.body; current = current.parentElement) {
    if (current.matches(ACTION_SELECTOR) || getComputedStyle(current).cursor === 'pointer') {
      return true
    }
  }
  return false
}

const isAction = (event: Event) => {
  if (event.type === 'submit') {
    return true
  }
  const element = elementOf(event.target)
  if (!element) {
    return false
  }
  if (event instanceof KeyboardEvent) {
    if (event.key === 'Enter') {
      return element.matches(FORM_FIELD_SELECTOR) || looksClickable(element)
    }
    return event.key === ' ' && looksClickable(element) && !element.matches('input, textarea, [contenteditable]')
  }
  return looksClickable(element)
}

// Reload, close the tab, zoom, copy: the dashboard binds no modified key, so these belong to the
// browser and stay usable on an expired tab. Enter keeps its guard since a modifier does not stop it
// from submitting.
const MODIFIER_KEYS = new Set(['Meta', 'Control', 'Alt', 'Shift'])

const isBrowserShortcut = (event: Event) =>
  event instanceof KeyboardEvent &&
  (/^F\d{1,2}$/.test(event.key) ||
    MODIFIER_KEYS.has(event.key) ||
    ((event.metaKey || event.ctrlKey || event.altKey) && event.key !== 'Enter'))

const block = (event: Event) => {
  event.preventDefault()
  event.stopImmediatePropagation()
}

export const useSessionActivity = () => {
  const router = useRouter()

  useEffect(() => {
    let gestureBlockedUntil = 0

    const blockGesture = (event: Event) => {
      block(event)
      gestureBlockedUntil = Date.now() + GESTURE_TAIL_MS
    }

    const onEvent = (event: Event) => {
      if (!isPinned() || isInsideSessionDialog(event.target) || isBrowserShortcut(event)) {
        return
      }
      if (Date.now() < gestureBlockedUntil) {
        block(event)
        return
      }
      const phase = sessionPhase()
      if (phase === 'dialog-open') {
        return
      }
      if (phase === 'expired') {
        blockGesture(event)
        openSessionDialog()
        return
      }
      if (phase === 'dismissed') {
        if (isAction(event)) {
          blockGesture(event)
          openSessionDialog()
        }
        return
      }
      if (!canProceed()) {
        blockGesture(event)
        handleNotAwaitedPromise(confirmSessionThenOpenDialog())
        return
      }
      if (ACTIVITY_EVENTS.has(event.type)) {
        recordActivity()
      }
    }

    const onReturn = () => {
      if (document.visibilityState === 'visible' && isPinned()) {
        handleNotAwaitedPromise(confirmSessionThenOpenDialog())
      }
    }

    GUARDED_EVENTS.forEach((type) => {
      globalThis.addEventListener(type, onEvent, { capture: true })
    })
    document.addEventListener('visibilitychange', onReturn)
    globalThis.addEventListener('focus', onReturn)
    const unsubscribe = router.subscribe('onResolved', () => {
      recordActivity()
    })
    return () => {
      GUARDED_EVENTS.forEach((type) => {
        globalThis.removeEventListener(type, onEvent, { capture: true })
      })
      document.removeEventListener('visibilitychange', onReturn)
      globalThis.removeEventListener('focus', onReturn)
      unsubscribe()
    }
  }, [router])

  useBlocker({
    shouldBlockFn: () => {
      if (canProceed()) {
        return false
      }
      handleNotAwaitedPromise(confirmSessionThenOpenDialog())
      return true
    },
    enableBeforeUnload: false,
  })
}
